import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import palette from 'constants/palette'
import React from 'react'
import { useStyletron } from 'styletron-react'

export interface LocationAddressRowProps {
  /** The location's display name (already normalized to camelCase at the slice). */
  name: string
  /** Pre-translated aria-label naming the location, e.g. "Remove Main St". */
  removeLabel: string
  /** Remove this row from the selection. */
  onRemove: () => void
  /**
   * Render the row without its remove control. View-only operators see the
   * active locations as read-only state, with no write affordance.
   */
  readOnly?: boolean
}

/**
 * A single removable location row for the Active Locations subset list (AI-31).
 * Presentational only — no data fetching, i18n-agnostic (the consumer passes the
 * pre-translated `removeLabel`). The remove control is a uikit `Button
 * kind="minimal"` (a real <button>, full keyboard support) rather than a
 * hand-rolled element; the chevron-size-collapse trap that forces the tiny chip
 * "×" in RewardMultiSelect to stay native does not apply to a full-width row, so
 * no shrinking BaseButton override is passed.
 */
const LocationAddressRow = ({
  name,
  removeLabel,
  onRemove,
  readOnly = false,
}: LocationAddressRowProps) => {
  const [css] = useStyletron()
  return (
    <div
      className={`d-flex align-items-center justify-content-between ${css({
        padding: '10px 16px',
      })}`}
    >
      <Text.BodyText4>{name}</Text.BodyText4>
      {!readOnly && (
        <Button kind="minimal" onClick={onRemove} aria-label={removeLabel}>
          {/* Grey (not the default link-blue) delete cross, per AI-901 design. */}
          <i
            className={`fa fa-times ${css({ color: palette.grey50 })}`}
            aria-hidden="true"
          />
        </Button>
      )}
    </div>
  )
}

export default LocationAddressRow
